import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import problemImg from "../../assets/global/Journey_problem.webp";
import solutionImg from "../../assets/global/Journey_solution.webp";
import styles from "./GlobalJourney.module.css";

const NOS = ["No confusing charts.", "No waiting in line.", "No awkward calls."];

// Border that enters at top-center, splits left + right down the sides,
// and meets at bottom-center, drawn by scroll progress.
function BorderTransfer({ progress, id }: { progress: any; id: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) =>
      setSize({ w: e.contentRect.width, h: e.contentRect.height })
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { w, h } = size;
  const r = 28;
  const p = 1.5;
  const cx = w / 2;
  const ready = w > 0 && h > 0;
  // left half: top-center -> left edge -> bottom-center
  const left = ready
    ? `M ${cx} ${p} L ${r + p} ${p} A ${r} ${r} 0 0 0 ${p} ${r + p} L ${p} ${h - r - p} A ${r} ${r} 0 0 0 ${r + p} ${h - p} L ${cx} ${h - p}`
    : "";
  // right half: top-center -> right edge -> bottom-center
  const right = ready
    ? `M ${cx} ${p} L ${w - r - p} ${p} A ${r} ${r} 0 0 1 ${w - p} ${r + p} L ${w - p} ${h - r - p} A ${r} ${r} 0 0 1 ${w - r - p} ${h - p} L ${cx} ${h - p}`
    : "";

  return (
    <div ref={wrapRef} className={styles.beamWrap} aria-hidden="true">
      {ready && (
        <svg className={styles.beam} width={w} height={h}>
          <defs>
            <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ed0687" />
              <stop offset="100%" stopColor="#fe6c0f" />
            </linearGradient>
          </defs>
          <motion.path
            className={styles.beamPath}
            d={left}
            pathLength={1}
            stroke={`url(#${id})`}
            style={{ pathLength: progress }}
          />
          <motion.path
            className={styles.beamPath}
            d={right}
            pathLength={1}
            stroke={`url(#${id})`}
            style={{ pathLength: progress }}
          />
        </svg>
      )}
    </div>
  );
}

export default function GlobalJourney() {
  // One scroll timeline drives the whole transfer: problem border draws,
  // the connector fires, then the solution border draws.
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 78%", "end 62%"],
  });

  const probDraw = useTransform(scrollYProgress, [0.02, 0.4], [0, 1]);
  const glowScale = useTransform(scrollYProgress, [0.38, 0.56], [0, 1]);
  const dotY = useTransform(scrollYProgress, [0.38, 0.56], [0, 90]);
  const dotOpacity = useTransform(
    scrollYProgress,
    [0.37, 0.4, 0.55, 0.57],
    [0, 1, 1, 0]
  );
  const solDraw = useTransform(scrollYProgress, [0.56, 0.94], [0, 1]);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-label="From confusion to clarity"
    >
      {/* ===== PROBLEM CARD ===== */}
      <div className={`${styles.card} ${styles.textLeft}`}>
        <img className={styles.bg} src={problemImg} alt="" loading="lazy" />
        <span className={`${styles.shade} ${styles.shadeLeft}`} />
        <BorderTransfer progress={probDraw} id="beamProblem" />
        <div className={styles.content}>
          <h2 className={styles.h2}>Some questions keep you up at night.</h2>
          <p className={styles.lead}>
            Love, career, money, timing, the moments that matter most rarely
            come with a clear answer.
          </p>
          <p className={styles.tagline}>
            Relationships · Career · Marriage · Money · Family · What's next
          </p>
        </div>
      </div>

      {/* ===== TRANSFER CONNECTOR ===== */}
      <div className={styles.connector}>
        <span className={styles.connectorTrack} />
        <motion.span className={styles.connectorGlow} style={{ scaleY: glowScale }} />
        <motion.span
          className={styles.connectorDot}
          style={{ y: dotY, opacity: dotOpacity }}
        />
      </div>

      {/* ===== SOLUTION CARD ===== */}
      <div className={`${styles.card} ${styles.textRight}`}>
        <img className={styles.bg} src={solutionImg} alt="" loading="lazy" />
        <span className={`${styles.shade} ${styles.shadeRight}`} />
        <BorderTransfer progress={solDraw} id="beamSolution" />
        <div className={styles.content}>
          <h2 className={styles.h2}>Your personal Vedic astrologer, powered by AI.</h2>
          <p className={styles.lead}>
            Aura reads your exact birth chart and answers in clear, everyday
            language, in seconds, any hour.
          </p>
          <div className={styles.nos}>
            {NOS.map((n) => (
              <span className={styles.no} key={n}>
                {n}
              </span>
            ))}
          </div>
          <p className={styles.punch}>Just ask. Aura answers.</p>
        </div>
      </div>
    </section>
  );
}
